import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  exportarLogGeralDe,
  FiltrosDoLogGeral,
  listarLogGeralDe,
  LogGeralItem,
  obterOpcoesDoLogGeralDe,
  OpcoesDoLogGeral,
  SistemaDoLog,
  SISTEMAS_DO_LOG,
} from '../../../api/auditLogsApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import Paginacao from '../../../components/paginacao/Paginacao';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import DetalheLogGeralModal from '../components/logs/DetalheLogGeralModal';
import FiltroLogGeralModal from '../components/logs/FiltroLogGeralModal';
import { descreverQuem, formatarQuando } from '../components/logs/formatacao';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

/**
 * Logs do sistema (rota `/admin/sistema/logs`) — transversal/at-91, ADR 0062.
 *
 * Tudo o que aconteceu na aplicação: quem, quando, o quê, em qual registro, de onde e se deu certo,
 * inclusive tentativas recusadas. ADMIN e TI (decisão do Victor, 24/10/2026).
 *
 * 🔴 Falha de carga NUNCA vira "nenhum registro": numa tela de auditoria, lista vazia por erro se lê
 * como "ninguém fez nada", a conclusão mais cara possível (regra da at-60).
 *
 * Paginação numerada, como as outras listas do sistema (pedido do Victor, 25/10/2026). ⚠️ Linhas
 * novas entram no topo o tempo todo, então a página N pode repetir ou pular um registro entre um
 * clique e outro — a hint diz isso, e o filtro de período é o jeito de fixar o recorte.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que entra no log',
    conteudo: (
      <p>
        Toda ação que altera alguma coisa no sistema, com ou sem sucesso: cadastrar, editar, mover o
        veículo, liberar, aprovar horas, configurar, entrar e sair. Também as que geram documento ou
        arquivo (PDF do checklist, relatórios, exportações), as rotinas automáticas e os avisos
        trocados com outros sistemas. Consultas comuns não entram, para a lista não se encher do que
        ninguém procura.
      </p>
    ),
  },
  {
    titulo: 'Quem fez',
    conteudo: (
      <>
        <p>
          <strong>Usuário</strong>: a pessoa, com o nome e o perfil que tinha no momento.{' '}
          <strong>Público (sem login)</strong>: o associado nos links do checklist e da pesquisa, o
          totem, e tentativas de entrar no sistema.
        </p>
        <p>
          <strong>Integração</strong>: outro sistema, como o SAAC. <strong>Sistema</strong>: as rotinas
          automáticas (limpezas da madrugada, avisos de prazo, sincronização com o Nasajon).
        </p>
      </>
    ),
  },
  {
    titulo: 'Uma linha por ação, e o que ela gravou',
    conteudo: (
      <p>
        Cada ação aparece numa linha. Ao abrir, o detalhe mostra o que ela gravou (por exemplo, a
        liberação de cada esteira) e, quando houver, o <strong>antes e depois</strong>. Os registros
        anteriores a 24/10/2026, de quando o log geral ainda não existia, aparecem como linhas soltas,
        sem rota e sem resultado.
      </p>
    ),
  },
  {
    titulo: 'O que nunca aparece',
    conteudo: (
      <p>
        Senhas, tokens e chaves de integração nunca são gravados. CPF, CNPJ e telefone aparecem
        mascarados. Arquivos enviados aparecem só com nome, tipo e tamanho.
      </p>
    ),
  },
  {
    titulo: 'Páginas',
    conteudo: (
      <p>
        A lista mostra 20 registros por página, do mais recente para o mais antigo. Novas ações
        entram no topo o tempo todo, então um registro pode aparecer de novo (ou ser pulado) ao
        passar de página. Para um recorte que não muda, use os <strong>Filtros</strong> com um
        período que já terminou.
      </p>
    ),
  },
  {
    titulo: 'Um log por sistema',
    conteudo: (
      <p>
        A oficina e os módulos que rodam à parte (<strong>Portal de Cotações</strong>,{' '}
        <strong>Requisições</strong>, a <strong>sincronização da base de dados</strong> e a{' '}
        <strong>tabela FIPE</strong>) têm cada um o seu registro. As abas no topo escolhem qual ler; só
        aparecem os sistemas ligados neste ambiente.
      </p>
    ),
  },
  {
    titulo: 'Ninguém edita nem apaga',
    conteudo: (
      <p>
        O log é imutável, inclusive para o administrador. Abrir o detalhe de um registro e exportar a
        lista também ficam registrados.
      </p>
    ),
  },
];

const colunas: ColunaTabela<LogGeralItem>[] = [
  { titulo: 'Quando', largura: 150, texto: (l) => formatarQuando(l.createdAt) },
  { titulo: 'Quem', largura: 220, larguraMaxima: 280, texto: descreverQuem },
  { titulo: 'O que', largura: 300, larguraMaxima: 460, texto: (l) => l.resumo },
  // O id inteiro fica no title da célula (a TabelaSistema o põe a partir de `texto`), e o filtro
  // "ver histórico" continua indo por `entidadeId`.
  { titulo: 'Registro', largura: 200, larguraMaxima: 280, texto: (l) => l.registro },
  // Linha anterior ao log geral, ou gravada fora de uma requisição (rotina), não tem rota: é
  // "fora de requisição", não um dado faltando.
  { titulo: 'Origem', largura: 200, larguraMaxima: 280, texto: (l) => (l.rota ? `${l.metodo ?? ''} ${l.rota}` : l.tipo === 'NEGOCIO' ? 'Registro interno' : '-') },
  {
    titulo: 'Resultado',
    largura: 110,
    texto: (l) => (l.sucesso ? 'Sucesso' : 'Falha'),
    render: (l) => (
      <span className={`cilia-badge ${l.sucesso ? 'badge-verde' : 'badge-vermelho'}`}>
        {l.sucesso ? 'Sucesso' : `Falha${l.statusCode ? ` (${l.statusCode})` : ''}`}
      </span>
    ),
  },
];

function descreverFiltros(f: FiltrosDoLogGeral): string {
  if (!f.de && !f.ate && f.entidadeId) return `Histórico completo deste registro (${f.entidade ?? 'registro'} ${f.entidadeId})`;
  const de = f.de ? formatarQuando(f.de).slice(0, 10) : 'últimos 7 dias';
  const ate = f.ate ? formatarQuando(f.ate).slice(0, 10) : 'agora';
  return f.de || f.ate ? `De ${de} até ${ate}` : 'Últimos 7 dias';
}

const LogsDoSistemaPage: React.FC = () => {
  const sistemas = SISTEMAS_DO_LOG.filter((x) => x.habilitado());
  const [sistema, setSistema] = useState<SistemaDoLog>('SOAC');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosDoLogGeral>({});
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [opcoes, setOpcoes] = useState<OpcoesDoLogGeral | null>(null);
  const [itens, setItens] = useState<LogGeralItem[]>([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  // Descarta resposta de uma carga anterior que chegue depois de uma nova (troca rápida de filtro).
  const geracao = useRef(0);

  useEffect(() => {
    setOpcoes(null);
    obterOpcoesDoLogGeralDe(sistema)
      .then(setOpcoes)
      .catch(() => {
        // A lista continua: só os campos de módulo e ação viram texto livre.
        notify.warning('Não foi possível carregar as opções dos filtros. Os campos de módulo e ação aceitam texto livre.', {
          toastId: 'log-geral-opcoes',
        });
      });
  }, [sistema]);

  const carregar = useCallback(async (s: SistemaDoLog, f: FiltrosDoLogGeral, p: number) => {
    const minha = ++geracao.current;
    setCarregando(true);
    setErro(false);
    try {
      const r = await listarLogGeralDe(s, f, p);
      if (minha !== geracao.current) return;
      setItens(r.itens);
      setTotal(r.total);
      setTotalPaginas(r.totalPaginas);
    } catch (error) {
      if (minha !== geracao.current) return;
      setItens([]);
      setTotal(null);
      setTotalPaginas(1);
      setErro(true);
      notify.errorFrom(error, 'Não foi possível carregar o log do sistema. Recarregue a página; se persistir, acione a TI.');
    } finally {
      if (minha === geracao.current) setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar(sistema, filtros, pagina);
  }, [carregar, sistema, filtros, pagina]);

  // Filtro novo ou troca de sistema voltam para a página 1 (a página 3 do recorte anterior
  // viraria lista vazia sem explicação — regra do modal de filtros).
  const aplicarFiltros = (f: FiltrosDoLogGeral) => {
    setPagina(1);
    setFiltros(f);
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const { blob, nome, cortado } = await exportarLogGeralDe(sistema, filtros);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nome;
      a.click();
      URL.revokeObjectURL(url);
      if (cortado) notify.warning('O arquivo foi cortado em 50 mil linhas. Estreite os filtros para exportar o resto.');
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível exportar o log. Tente de novo; se persistir, acione a TI.');
    } finally {
      setExportando(false);
    }
  };

  const verHistorico = (entidade: string, entidadeId: string) => {
    setDetalheId(null);
    aplicarFiltros({ entidade, entidadeId, incluirNegocio: true });
  };

  return (
    <div
      className="Frame1000004012"
      style={{ height: 'fit-content', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Logs do sistema
            <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
              ?
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="admin-btn" onClick={() => setFiltrosAbertos(true)}>
              Filtros
            </button>
            <button type="button" className="admin-btn" onClick={exportar} disabled={exportando || erro}>
              {exportando ? 'Exportando…' : 'Exportar CSV'}
            </button>
          </div>
        </div>

        <p className="admin-hint">
          Tudo o que foi feito no sistema: quem, quando, o quê e se deu certo, inclusive as tentativas
          recusadas. Ninguém edita nem apaga estes registros.
        </p>
        {sistemas.length > 1 && (
          <div className="recepcao-tabs">
            {sistemas.map((x) => (
              <button
                key={x.valor}
                type="button"
                className={`recepcao-tab ${sistema === x.valor ? 'active' : ''}`}
                onClick={() => {
                  if (x.valor === sistema) return;
                  setItens([]);
                  setTotal(null);
                  aplicarFiltros({});
                  setSistema(x.valor);
                }}
              >
                {x.rotulo}
              </button>
            ))}
          </div>
        )}

        {erro && (
          <p className="erro-de-tela">
            Não foi possível carregar o log do sistema. Nada pode ser concluído sobre o período enquanto
            a consulta não responder. Recarregue a página; se persistir, acione a TI.
          </p>
        )}

        {/* Mesmo casco das tabelas da Consumo de IA (`CardDeTabela`): cartão com o recorte no título. */}
        {!erro && (
          <div className="admin-card" style={{ marginTop: 16 }}>
            <div className="admin-card-title">
              {descreverFiltros(filtros)}
              {total !== null && !carregando && (
                <span className="admin-texto-secundario" style={{ fontSize: 14, fontWeight: 'normal' }}>
                  {total.toLocaleString('pt-BR')} {total === 1 ? 'registro' : 'registros'}
                </span>
              )}
            </div>
            <TabelaSistema
              colunas={colunas}
              itens={itens}
              chave={(l) => l.id}
              vazio="Nenhum registro no período e com estes filtros."
              carregando={carregando}
              onClickLinha={(l) => setDetalheId(l.id)}
            />
            {totalPaginas > 1 && (
              <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
            )}
          </div>
        )}
      </div>

      {filtrosAbertos && (
        <FiltroLogGeralModal
          filtrosAplicados={filtros}
          opcoes={opcoes}
          onClose={() => setFiltrosAbertos(false)}
          onAplicar={(f) => {
            setFiltrosAbertos(false);
            aplicarFiltros(f);
          }}
        />
      )}

      {detalheId && <DetalheLogGeralModal sistema={sistema} id={detalheId} onClose={() => setDetalheId(null)} onVerHistorico={verHistorico} />}

      <ComoFunciona isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} titulo="Como Funciona - Logs do sistema" secoes={helpSections} />
    </div>
  );
};

export default LogsDoSistemaPage;
